import { useCallback, useEffect, useMemo, useState } from "react";
import { normalizeLocale, normalizeUnitSystem } from "@plaatplan/i18n";
import {
  createDisplayFromPreferences,
  defaultLocalePreferences,
  loadLocalePreferences,
  saveLocalePreferences,
} from "./localePreferences.js";
import { LocaleContext } from "./useLocale.js";

/**
 * @typedef {import("@plaatplan/i18n").UnitSystem} UnitSystem
 * @typedef {{ locale: string, unitSystem: UnitSystem }} LocalePreferences
 * @typedef {{
 *   display: ReturnType<typeof createDisplayFromPreferences>,
 *   preferences: LocalePreferences,
 *   setLocale: (locale: string) => void,
 *   syncLocaleFromRoute: (locale: string) => void,
 *   setUnitSystem: (unitSystem: UnitSystem) => void,
 * }} LocaleContextValue
 */

/** @param {{ children: import("react").ReactNode }} props */
export function LocaleProvider({ children }) {
  const [preferences, setPreferences] = useState(loadLocalePreferences);

  const display = useMemo(
    () => createDisplayFromPreferences(preferences),
    [preferences],
  );

  useEffect(() => {
    document.documentElement.lang = display.localeTag;
  }, [display.localeTag]);

  const persist = useCallback((next) => {
    const resolved = {
      locale: normalizeLocale(next.locale),
      unitSystem: normalizeUnitSystem(next.unitSystem),
    };
    setPreferences(resolved);
    saveLocalePreferences(resolved);
  }, []);

  const setLocale = useCallback((locale) => {
    persist({ ...preferences, locale });
  }, [persist, preferences]);

  const syncLocaleFromRoute = useCallback((locale) => {
    persist({ ...preferences, locale: normalizeLocale(locale) });
  }, [persist, preferences]);

  const setUnitSystem = useCallback((unitSystem) => {
    persist({ ...preferences, unitSystem });
  }, [persist, preferences]);

  const value = useMemo(() => ({
    display,
    preferences,
    setLocale,
    syncLocaleFromRoute,
    setUnitSystem,
  }), [display, preferences, setLocale, syncLocaleFromRoute, setUnitSystem]);

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
}

export { defaultLocalePreferences };
